import React, { useState, useEffect } from 'react';
import { Button, Typography, Box, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, CircularProgress, Tooltip, TextField } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, utcToZonedTime } from 'date-fns-tz';
import { format as formatDate } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import './ProtectedMessage.css';

const fetchMessage = async (date: string, token: string): Promise<{ message: string, status: number }> => {
    const endpoint = process.env.REACT_APP_API_ENDPOINT;

    const response = await fetch(`${endpoint}/messages?date=${date}`, {
        headers: {
            'Authorization': `Bearer ${token}`
        }
    });

    if (response.status === 401) {
        return { message: 'Unauthorized', status: 401 };
    }

    if (response.ok) {
        const data = await response.json();
        return { message: data[0] || '<p>No message for this date.</p>', status: 200 };
    } else {
        return { message: '<p>Failed to fetch message.</p>', status: response.status };
    }
};

const ProtectedMessage: React.FC<{ isAdmin: boolean }> = ({ isAdmin }) => {
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
    const [authError, setAuthError] = useState(false);
    const [open, setOpen] = useState(false);
    const [isAdminState, setAdminState] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchInitialMessage = async () => {
            setLoading(true);
            const token = localStorage.getItem('token');
            if (!token) {
                return;
            }
            setAdminState(JSON.parse(atob(token.split('.')[1])).admin);
            const timeZone = 'America/Chicago'; // Central Time
            const zonedDate = selectedDate ? utcToZonedTime(selectedDate, timeZone) : null;
            const dateStr = zonedDate ? format(zonedDate, 'yyyy-MM-dd') : '';
            const result = await fetchMessage(dateStr, token);
            if (result.status === 401) {
                setAuthError(true);
            } else {
                const formattedDate = selectedDate ? formatDate(selectedDate, 'MMMM do, yyyy') : '';
                const messageWithDate = `<h3>${formattedDate}:</h3>${result.message}`;
                setMessage(messageWithDate);
            }
            setLoading(false);
        };

        fetchInitialMessage();
    }, [selectedDate, navigate]);

    const handleDateChange = (date: Date | null) => {
        setSelectedDate(date);
    };

    const handleLogout = () => {
        localStorage.removeItem('token');
        navigate('/login');
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleAuthErrorClose = () => {
        setAuthError(false);
    };

    return (
        <Box className="protected-message" sx={{ textAlign: 'center', minHeight: '400px', display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
            {loading ? (
                <CircularProgress />
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                    <Typography variant="h5" component="h1" color="primary">
                        Protected Message
                    </Typography>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                            label="Select Date"
                            value={selectedDate}
                            onChange={handleDateChange}
                            renderInput={(params) => <TextField {...params} fullWidth margin="normal" />}
                        />
                    </LocalizationProvider>
                    <Box
                        sx={{
                            flexGrow: 1,
                            overflowY: 'auto',
                            textAlign: 'left',
                            mt: 2,
                            p: 2,
                            border: '1px solid #ccc',
                            borderRadius: '8px',
                        }}
                        dangerouslySetInnerHTML={{ __html: message }}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 2 }}>
                        <Button onClick={handleLogout} variant="contained" color="primary">
                            Logout
                        </Button>
                        <Tooltip title={isAdminState ? '' : 'You need to be an admin to access this page'}>
                            <span>
                                <Button
                                    onClick={() => navigate('/add-message')}
                                    variant="contained"
                                    color="secondary"
                                    sx={{ ml: 2 }}
                                    disabled={!isAdminState}
                                >
                                    Add Message
                                </Button>
                            </span>
                        </Tooltip>
                    </Box>
                </Box>
            )}
            <Dialog open={open} onClose={handleClose}>
                <DialogTitle>Incorrect Password</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        The username or password you entered is incorrect. Please try again.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} color="primary">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={authError} onClose={handleAuthErrorClose}>
                <DialogTitle>Invalid Token</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Your session has expired or is invalid. Please log in again.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleAuthErrorClose} color="primary">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ProtectedMessage;